import { createColumnHelper } from "@tanstack/react-table";
import { formatDate } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm, DATE_FORMATS_FNS, ReactTable, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/ExactMonitoring/components/Billing/components/FailedInvoicesModal/FailedInvoicesModal.scss";
import { useDeleteFailedInvoiceMutation, useRetryFailedInvoiceMutation } from "modules/ExactMonitoring/components/Billing/hooks";

import type { ExactFailedInvoice } from "models";

type FailedInvoiceModalProps = {
  isOpen: boolean;
  exactFailedInvoices: ExactFailedInvoice[];
  dealerId?: number;
  dealerLocationId?: number;
  onClose: () => void;
};

const columnHelper = createColumnHelper<ExactFailedInvoice>();

export const FailedInvoicesModal = ({ isOpen, dealerId, dealerLocationId, exactFailedInvoices, onClose }: FailedInvoiceModalProps) => {
  const { t } = useTranslation();

  const canRetryFailedInvoice = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ExactInvoice);
  const canDeleteFailedInvoice = useCan(ACL_ACTIVITIES.Delete, ACL_RESOURCES.ExactInvoice);

  const [selectedRetryFailedInvoiceId, setSelectedRetryFailedInvoiceId] = useState(0);
  const [selectedDeleteFailedInvoiceId, setSelectedDeleteFailedInvoiceId] = useState(0);
  const { mutateAsync: retryFailedInvoice, isPending: isRetryingFailedInvoice, variables: retryPayload } = useRetryFailedInvoiceMutation();
  const { mutateAsync: deleteFailedInvoice, isPending: isDeletingFailedInvoice, variables: deletePayload } = useDeleteFailedInvoiceMutation();

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "time",
        size: 200,
        header: () => <span className="no-wrap-text"> {t("v8_time", "Time")} </span>,
        cell: ({
          row: {
            original: { created_on }
          }
        }) => <span> {formatDate(created_on, DATE_FORMATS_FNS.dateMonthYearTimeWithSeconds)} </span>
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        size: 200,
        header: () => "",
        cell: ({
          row: {
            original: { id }
          }
        }) => {
          return (
            <div className="button-container">
              <Button
                size="small"
                color="orange"
                disabled={!canRetryFailedInvoice}
                onClick={() => setSelectedRetryFailedInvoiceId(id)}
                loading={isRetryingFailedInvoice && retryPayload.exact_failed_invoice_id === id}
              >
                {t("v8_retry_failed_invoice", "Retry failed invoice")}
              </Button>

              <Button
                size="small"
                color="red"
                disabled={!canDeleteFailedInvoice}
                onClick={() => setSelectedDeleteFailedInvoiceId(id)}
                loading={isDeletingFailedInvoice && deletePayload.exact_failed_invoice_id === id}
              >
                {t("v8_delete_failed_invoice", "Delete failed invoice")}
              </Button>
            </div>
          );
        }
      })
    ],
    [isDeletingFailedInvoice, isRetryingFailedInvoice, deletePayload, retryPayload, canRetryFailedInvoice, canDeleteFailedInvoice, t]
  );

  if (!dealerId && !dealerLocationId) return null;

  const handleRetryFailedInvoice = async () => {
    setSelectedRetryFailedInvoiceId(0);
    if (!selectedRetryFailedInvoiceId) return;
    await retryFailedInvoice({ exact_failed_invoice_id: selectedRetryFailedInvoiceId, dealer_id: dealerId, dealer_location_id: dealerLocationId });
  };

  const handleDeleteFailedInvoice = async () => {
    setSelectedDeleteFailedInvoiceId(0);
    if (!selectedDeleteFailedInvoiceId) return;
    await deleteFailedInvoice({ exact_failed_invoice_id: selectedDeleteFailedInvoiceId, dealer_id: dealerId, dealer_location_id: dealerLocationId });
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose} closeOnDimmerClick={false} className="FailedInvoicesModal">
        <Modal.Header>
          <div className="header">
            <span>{t("v8_failed_invoices", "Failed Invoices")}</span>
            <Button className="modal-close-btn" basic onClick={onClose}>
              <Icon className="xmark grey" />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <ReactTable columns={columns} data={exactFailedInvoices} />
        </Modal.Content>
      </Modal>

      <CustomConfirm
        isLoading={isRetryingFailedInvoice}
        isOpen={Boolean(selectedRetryFailedInvoiceId)}
        type={CUSTOM_CONFIRM_TYPES.Danger}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={t("v8_retry_failed_invoice_message", "Are you sure that you want to retry failed invoice? You can't undo this action.")}
        handleConfirm={handleRetryFailedInvoice}
        handleCancel={() => setSelectedRetryFailedInvoiceId(0)}
      />

      <CustomConfirm
        isLoading={isDeletingFailedInvoice}
        isOpen={Boolean(selectedDeleteFailedInvoiceId)}
        type={CUSTOM_CONFIRM_TYPES.Danger}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={t("v8_delete_failed_invoice_message", "Are you sure that you want to delete failed invoice? You can't undo this action.")}
        handleConfirm={handleDeleteFailedInvoice}
        handleCancel={() => setSelectedDeleteFailedInvoiceId(0)}
      />
    </>
  );
};
